'use client'

import { useEffect } from 'react'
import { usePathname } from 'next/navigation'

export default function PageBodyClass() {
  const pathname = usePathname()

  useEffect(() => {
    // Remove all existing page classes
    const existingClasses = Array.from(document.body.classList).filter(cls => 
      cls.startsWith('page-') || 
      cls === 'work-detail-page'
    )
    existingClasses.forEach(cls => document.body.classList.remove(cls))

    // Generate page classes based on pathname
    const generatePageClasses = (path: string): string[] => {
      const classes: string[] = []
      
      // Remove leading slash and split into segments
      const segments = path.replace(/^\//, '').split('/').filter(Boolean)
      
      // Handle root/home page
      if (segments.length === 0) {
        classes.push('page-home')
        return classes
      }
      
      // Handle single-level pages (about, works, contact, etc.)
      if (segments.length === 1) {
        const pageSlug = segments[0]
        classes.push(`page-${pageSlug}`)
        return classes
      }
      
      // Handle work detail pages (works/work-title)
      if (segments.length === 2 && segments[0] === 'work') {
        const workSlug = segments[1]
        classes.push(`page-${workSlug}`)
        classes.push('work-detail-page') // Keep existing class for compatibility
        return classes
      }
      
      // Handle other multi-level pages
      if (segments.length >= 2) {
        // Add class for the full path slug
        const fullSlug = segments.join('-')
        classes.push(`page-${fullSlug}`)
        
        // Add parent section class
        classes.push(`page-${segments[0]}`)
        return classes
      }
      
      return classes
    }

    // Generate and apply page classes
    const pageClasses = generatePageClasses(pathname)
    pageClasses.forEach(cls => document.body.classList.add(cls))

    // Cleanup function to remove classes when component unmounts
    return () => {
      pageClasses.forEach(cls => document.body.classList.remove(cls))
    }
  }, [pathname])

  // This component doesn't render anything
  return null
}
